import React from 'react';
import { TableCell, TableHead, TableRow, IconButton, Tooltip, Box } from '@mui/material';
import { ArrowUpward, ArrowDownward, UnfoldMore } from '@mui/icons-material';
import { SortDirection } from '../../utils/tableSorting';

interface SortableTableHeadProps {
  columns: Array<{
    key: string;
    label: string;
    sortable?: boolean;
    width?: string | number;
    align?: 'left' | 'center' | 'right';
  }>;
  sortConfig: {
    key: string;
    direction: SortDirection;
  };
  onSort: (key: string) => void;
  stickyHeader?: boolean;
}

const SortableTableHead: React.FC<SortableTableHeadProps> = ({
  columns,
  sortConfig,
  onSort,
  stickyHeader = false
}) => {
  const getSortIcon = (direction: SortDirection) => {
    switch (direction) {
      case 'asc':
        return <ArrowUpward fontSize="small" />;
      case 'desc':
        return <ArrowDownward fontSize="small" />;
      case 'none':
      default:
        return <UnfoldMore fontSize="small" />;
    }
  };

  // Debug logging
  //console.log('SortableTableHead render:', { sortConfig, columns: columns.map(c => c.key) });

  return (
    <TableHead>
      <TableRow sx={{
        backgroundColor: 'primary.main',
        '& .MuiTableCell-head': {
          color: 'white',
          fontWeight: 'bold',
          fontSize: '0.875rem',
          borderBottom: '2px solid',
          borderBottomColor: 'primary.dark',
        }
      }}>
        {columns.map((column) => (
          <TableCell
            key={column.key}
            align={column.align || 'left'}
            padding={column.key === 'checkbox' ? 'checkbox' : 'normal'}
            sx={{
              width: column.width,
              position: stickyHeader ? 'sticky' : 'static',
              top: stickyHeader ? 0 : 'auto',
              zIndex: stickyHeader ? 1 : 'auto',
              backgroundColor: 'primary.main',
              cursor: column.sortable !== false ? 'pointer' : 'default',
              '&:hover': column.sortable !== false ? {
                backgroundColor: 'primary.dark',
              } : {}
            }}
            onClick={() => column.sortable !== false && onSort(column.key)}
          >
            {column.key === 'checkbox' ? (
              column.label
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span>{column.label}</span>
                {column.sortable !== false && (
                  <Tooltip title={`Sort by ${column.label}`}>
                  <IconButton
                    key={`${column.key}-${sortConfig.key}-${sortConfig.direction}`}
                    size="small"
                    sx={{
                      color: 'white',
                      padding: '2px',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    {sortConfig.key === column.key ? getSortIcon(sortConfig.direction) : getSortIcon('none')}
                  </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

export default SortableTableHead;
